import { useCallback, useEffect, useMemo } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { observer } from "mobx-react-lite";
import { FormProvider, useForm } from "react-hook-form";
import { z } from "zod";
import { Button, Input, Modal } from "@synthesis/shared-ui";

import { AdminModalBody, AdminModalFooter } from "../../../components/admin-console/admin-modal";
import { type WorkTypeFormValues, usePageStore } from "../reference-document-types-page.lib";

const workTypeSchema = z.object({
  name: z.string().trim().min(1, "Укажите наименование"),
  code: z.string().trim().optional(),
});

const EMPTY_FORM: WorkTypeFormValues = { name: "", code: "" };

/** Модалки «Новый вид работы» / «Редактирование вида работы». Открываются через стор. Бэка нет — сохранение только закрывает окно. */
export const WorkTypeModal = observer(() => {
  const store = usePageStore()!;
  const action = store.popupAction;
  const isOpen = action?.type === "add-work-type" || action?.type === "edit-work-type";
  const isEdit = action?.type === "edit-work-type";
  const workType = action?.type === "edit-work-type" ? action.workType : undefined;

  const defaults = useMemo<WorkTypeFormValues>(() => (workType ? { name: workType.displayName, code: workType.code } : EMPTY_FORM), [workType]);
  const methods = useForm<WorkTypeFormValues>({ resolver: zodResolver(workTypeSchema), mode: "onChange", defaultValues: defaults });

  // При каждом открытии — префилл формы (для «Редактирования» данные выбранного вида работ).
  useEffect(() => {
    if (isOpen) {
      methods.reset(defaults);
    }
  }, [defaults, isOpen, methods]);

  const handleClose = useCallback(() => {
    methods.reset(EMPTY_FORM);
    store.setPopupAction(null);
  }, [methods, store]);

  return (
    <Modal.Root isOpen={isOpen} setIsOpen={(open) => (open ? null : handleClose())}>
      <Modal.Content className="w-[43.5rem] max-w-[90vw]">
        <Modal.Title>{isEdit ? "Редактирование вида работы" : "Новый вид работы"}</Modal.Title>
        <FormProvider {...methods}>
          <AdminModalBody>
            <h4 className="heading-h4 text-type-common">{isEdit ? "Редактирование вида работы" : "Новый вид работы"}</h4>

            <div className="flex flex-col gap-4">
              <Input name="name" label="Наименование" required placeholder="Наименование вида работ" />
              <Input name="code" label="Код" placeholder="Код значения" />
            </div>
          </AdminModalBody>

          <AdminModalFooter className={isEdit ? "justify-between" : undefined}>
            {isEdit ? (
              <>
                {/* TODO(FRONT-1390): сохранение/удаление вида работы — после появления бэкенда. */}
                <Button theme="primary" size="lg" onClick={methods.handleSubmit(handleClose)}>
                  Сохранить
                </Button>
                <Button theme="secondary" size="lg" onClick={handleClose}>
                  Удалить
                </Button>
              </>
            ) : (
              <div className="flex items-center gap-4">
                {/* TODO(FRONT-1390): добавление вида работы — после появления бэкенда. */}
                <Button theme="primary" size="lg" onClick={methods.handleSubmit(handleClose)}>
                  Добавить
                </Button>
                <Button theme="secondary" size="lg" onClick={handleClose}>
                  Отмена
                </Button>
              </div>
            )}
          </AdminModalFooter>
        </FormProvider>
      </Modal.Content>
    </Modal.Root>
  );
});
